"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import {
  User,
  Phone,
  Mail,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  UserX,
  Shield,
  Settings,
} from "lucide-react";

function UnsubscribeSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [debugInfo, setDebugInfo] = useState("");

  // Google Apps Script web app URL - same as ContactHero
  const GOOGLE_SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbx78Y8WRIyWyufP0Awn6hFMI6GXVVhPotIncuIPjirPvQmlayE4xlndrXTrauT6hHKT/exec";

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear any previous status when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
      setDebugInfo("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setDebugInfo("");

    try {
      // Log the data being sent for debugging
      console.log("Submitting unsubscribe data:", formData);

      // Create form data for Google Apps Script
      const formDataToSend = new FormData();
      formDataToSend.append("formType", "unsubscribe"); // Important: tells the script this is an unsubscribe request
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("message", formData.message);
      formDataToSend.append("timestamp", new Date().toISOString());

      // Log FormData contents
      console.log("FormData contents:");
      for (let [key, value] of formDataToSend.entries()) {
        console.log(key, value);
      }

      // Try with different approaches
      let response;
      let responseText = "";

      try {
        // First attempt: Try with cors mode to get response
        response = await fetch(GOOGLE_SCRIPT_URL, {
          method: "POST",
          body: formDataToSend,
          mode: "cors",
        });

        if (response.ok) {
          responseText = await response.text();
          console.log("Response text:", responseText);
        }
      } catch (corsError) {
        console.log("CORS request failed, trying no-cors mode:", corsError);

        // Fallback: Use no-cors mode
        response = await fetch(GOOGLE_SCRIPT_URL, {
          method: "POST",
          body: formDataToSend,
          mode: "no-cors",
        });
      }

      // Alternative approach: Try with URL-encoded data
      if (!response || !response.ok) {
        console.log("Trying with URL-encoded data...");

        const urlEncodedData = new URLSearchParams();
        urlEncodedData.append("formType", "unsubscribe");
        urlEncodedData.append("name", formData.name);
        urlEncodedData.append("email", formData.email);
        urlEncodedData.append("phone", formData.phone);
        urlEncodedData.append("message", formData.message);
        urlEncodedData.append("timestamp", new Date().toISOString());

        response = await fetch(GOOGLE_SCRIPT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: urlEncodedData,
          mode: "no-cors",
        });
      }

      // Since we might be using no-cors mode, we can't always read the response
      // But if we reach here without error, it likely succeeded
      setSubmitStatus("success");
      setDebugInfo(`Unsubscribe request submitted at ${new Date().toLocaleTimeString()}`);

      // Reset form on success
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });

      console.log("Unsubscribe request submitted successfully to Google Sheets");

      // Additional debugging: Try to ping the script URL to verify it's accessible
      setTimeout(async () => {
        try {
          const testResponse = await fetch(GOOGLE_SCRIPT_URL, {
            method: "GET",
            mode: "no-cors",
          });
          console.log("Script URL is accessible");
        } catch (testError) {
          console.log("Script URL test failed:", testError);
        }
      }, 1000);

    } catch (error) {
      console.error("Error submitting unsubscribe request:", error);
      setSubmitStatus("error");
      setDebugInfo(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #1719A8 0%, #0F1280 100%)",
      }}
      role="main"
      aria-label="Manage subscription section"
    >
      {/* Top Wave */}
      <div className="absolute top-0 left-0 right-0" aria-hidden="true">
        <svg viewBox="0 0 1200 80" className="w-full h-auto" fill="#eff6ff">
          <path
            d="M0,40 C300,0 500,80 800,40 C1000,10 1100,60 1200,40 L1200,0 L0,0 Z"
            style={{
              fill: "linear-gradient(135deg, #1719A8 0%, #0F1280 100%)",
            }}
          />
        </svg>
      </div>

      {/* Glowing Background Blobs */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute top-20 left-10 w-96 h-96 rounded-full opacity-20 blur-3xl animate-pulse"
          style={{
            background: "radial-gradient(circle, #D96F2B 0%, transparent 70%)",
          }}
        ></div>
        <div
          className="absolute bottom-20 right-10 w-80 h-80 rounded-full opacity-15 blur-2xl"
          style={{
            background: "radial-gradient(circle, #C9C51A 0%, transparent 70%)",
          }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-10 blur-xl"
          style={{
            background: "radial-gradient(circle, #E8ECFF 0%, transparent 70%)",
          }}
        ></div>
      </div>

      {/* Floating Transparent Circles */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute top-32 right-1/4 w-24 h-24 bg-white/5 rounded-full backdrop-blur-sm animate-float"></div>
        <div className="absolute bottom-1/3 left-1/4 w-16 h-16 bg-white/10 rounded-full backdrop-blur-sm animate-float-delayed"></div>
        <div className="absolute top-2/3 right-1/3 w-32 h-32 bg-white/5 rounded-full backdrop-blur-sm animate-float"></div>
      </div>

      {/* Layered Liquid Shapes */}
      <div className="absolute inset-0" aria-hidden="true">
        <svg viewBox="0 0 1200 800" className="w-full h-full opacity-10">
          <defs>
            <linearGradient
              id="liquidGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#D96F2B" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#C9C51A" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#E8ECFF" stopOpacity="0.1" />
            </linearGradient>
          </defs>
          <path
            d="M0,200 C300,100 500,300 800,200 C1000,150 1100,250 1200,200 L1200,600 C900,500 700,700 400,600 C200,550 100,650 0,600 Z"
            fill="url(#liquidGradient)"
          />
        </svg>
      </div>

      {/* Floating Subscription Icons */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute top-20 left-10 w-8 h-8 bg-white/20 rounded-lg animate-float flex items-center justify-center"
          style={{ animationDelay: "0s" }}
        >
          <Settings size={16} className="text-white/60" />
        </div>
        <div
          className="absolute top-40 right-20 w-6 h-6 bg-[#D96F2B]/30 rounded-lg animate-float flex items-center justify-center"
          style={{ animationDelay: "2s" }}
        >
          <UserX size={12} className="text-white/60" />
        </div>
        <div
          className="absolute bottom-40 left-1/4 w-10 h-10 bg-[#C9C51A]/20 rounded-lg animate-float flex items-center justify-center"
          style={{ animationDelay: "4s" }}
        >
          <Shield size={20} className="text-white/60" />
        </div>
        <div
          className="absolute bottom-20 right-1/3 w-7 h-7 bg-white/15 rounded-lg animate-float flex items-center justify-center"
          style={{ animationDelay: "1s" }}
        >
          <Mail size={14} className="text-white/60" />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto py-4 lg:py-10 px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-start gap-[80px]">
          {/* LEFT CONTENT - Manage Subscription */}
          <div
            className={`w-full lg:w-[60%] transition-all duration-1000 ease-out ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
          >
            {/* Subscription Badge */}
            <div className="inline-flex items-center px-6 py-2 bg-white/80 backdrop-blur-md border border-white/60 rounded-full mb-2 shadow-lg">
              <span className="w-2 h-2 bg-[#1719A8] rounded-full mr-3 animate-pulse"></span>
              <span className=" text-sm font-bold uppercase tracking-wider text-[#1719A8]">
                Subscription Management
              </span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-black leading-normal text-white mb-6">
              Manage Your
              <span
                className="block  bg-clip-text text-transparent pb-3 bg-gradient-to-r from-orange-400 via-yellow-400 to-green-400 "

              >
                Subscription
              </span>
              Preferences
            </h1>

            <div className="space-y-6 mb-8">
              <p className="text-lg text-white/80 leading-relaxed max-w-2xl">
                Take control of your subscription preferences. Whether you want to
                update your communication settings, modify your subscription plan,
                or unsubscribe from our services, we make it simple and transparent.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl">
                  <div className="w-8 h-8 bg-[#C9C51A]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Settings size={16} className="text-[#C9C51A]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Update Preferences</h3>
                    <p className="text-white/70 text-sm">
                      Customize your communication preferences and subscription settings
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl">
                  <div className="w-8 h-8 bg-[#D96F2B]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Shield size={16} className="text-[#D96F2B]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Privacy Control</h3>
                    <p className="text-white/70 text-sm">
                      Your privacy matters. Manage your data and communication preferences
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <UserX size={16} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Easy Unsubscribe</h3>
                    <p className="text-white/70 text-sm">
                      Simple and hassle-free unsubscribe process with immediate effect
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-sm text-white/60 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
              <p className="mb-2">
                <strong className="text-white/80">Need help?</strong> Our support team is available 24/7 to assist you with any subscription-related questions.
              </p>
              <p>
                All changes take effect immediately, and you'll receive a confirmation email within minutes.
              </p>
            </div>
          </div>

          {/* RIGHT CONTENT - Unsubscribe Form */}
          <div
            className={`w-full lg:w-[40%] transition-all duration-1000 ease-out delay-300 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
          >
            <div className="relative w-full max-w-md mx-auto">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-4 md:p-8 shadow-2xl">
                {/* Form Header */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#D96F2B] to-[#C9C51A] rounded-full flex items-center justify-center mx-auto mb-4">
                    <UserX size={24} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Unsubscribe Request
                  </h2>
                  <p className="text-white/80 text-sm">
                    We're sorry to see you go. Please fill out the form below to process your unsubscribe request.
                  </p>
                </div>

                {/* Success/Error Messages */}
                {submitStatus === "success" && (
                  <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-xl flex items-center gap-2 text-green-300">
                    <CheckCircle size={18} />
                    <div>
                      <div className="text-sm font-medium">
                        Unsubscribe request submitted successfully! You will be removed from our mailing list within 24 hours.
                      </div>
                      {debugInfo && (
                        <div className="text-xs text-green-200 mt-1">
                          {debugInfo}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-xl flex items-center gap-2 text-red-300">
                    <AlertCircle size={18} />
                    <div>
                      <div className="text-sm font-medium">
                        Failed to process unsubscribe request. Please try again or contact support.
                      </div>
                      {debugInfo && (
                        <div className="text-xs text-red-200 mt-1">
                          {debugInfo}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Unsubscribe Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name Field */}
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60">
                      <User size={18} />
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your Full Name"
                      required
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#C9C51A] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Your full name"
                    />
                  </div>

                  {/* Email Field */}
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60">
                      <Mail size={18} />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address to Unsubscribe"
                      required
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#C9C51A] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Email address to unsubscribe"
                    />
                  </div>

                  {/* Phone Field */}
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60">
                      <Phone size={18} />
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Phone Number (Optional)"
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#C9C51A] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Your phone number (optional)"
                    />
                  </div>

                  {/* Message Field */}
                  <div className="relative">
                    <div className="absolute left-3 top-4 text-white/60">
                      <MessageSquare size={18} />
                    </div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Reason for unsubscribing (Optional) - Your feedback helps us improve"
                      rows={4}
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#C9C51A] focus:border-transparent transition-all duration-300 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Reason for unsubscribing (optional)"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3 px-6 rounded-xl font-semibold text-[#1719A8] bg-gradient-to-r from-[#C9C51A] to-[#D96F2B] hover:from-[#D96F2B] hover:to-[#C9C51A] transition-all duration-300 transform hover:scale-105 shadow-lg ${
                      isSubmitting
                        ? "opacity-70 cursor-not-allowed transform-none"
                        : ""
                    }`}
                    aria-label="Submit unsubscribe request"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-[#1719A8] border-t-transparent rounded-full animate-spin"></div>
                        Processing Request...
                      </div>
                    ) : (
                      "Unsubscribe"
                    )}
                  </button>
                </form>

                {/* Privacy Notice */}
                <div className="mt-4 text-center">
                  <p className="text-white/60 text-xs leading-relaxed">
                    Your privacy is important to us. This information will only be used to process your unsubscribe request and improve our services.
                  </p>
                  <p className="text-white/60 text-xs mt-1">
                    ⚡ Powered by Google Sheets integration
                  </p>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#C9C51A]/80 rounded-full animate-pulse"></div>
                <div
                  className="absolute -bottom-2 -left-2 w-4 h-4 bg-[#D96F2B]/80 rounded-full animate-pulse"
                  style={{ animationDelay: "1s" }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
        <svg viewBox="0 0 1200 120" className="w-full h-auto">
          <path
            d="M0,60 C300,120 500,0 800,60 C1000,100 1100,40 1200,60 L1200,120 L0,120 Z"
            fill="#0B1026"
          />
        </svg>
      </div>

      {/* Custom Animations */}
      <style >{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-25px) rotate(5deg);
          }
        }
        @keyframes float-delayed {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(-3deg);
          }
        }
        .animate-float {
          animation: float 10s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 12s ease-in-out infinite 4s;
        }
      `}</style>
    </section>
  );
}

export default UnsubscribeSection;
